import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'
   * @constant {number}
   */
  #animationDelay = 150;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove('desktop:hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add('hidden');
    } else {
      this.refs.headerDrawerContainer.classList.add('desktop:hidden');
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove('hidden');
    }
  }

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      this.removeAttribute('data-animating');

      if (isAtTop) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';
      // delay transitioning to idle hidden state for hiding animation
      this.setAttribute('data-animating', '');

      this.#timeout = setTimeout(() => {
        this.dataset.stickyState = 'idle';
        this.removeAttribute('data-animating');
      }, this.#animationDelay);
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});

// Hide unavailable decoration options based on quantity
document.addEventListener('DOMContentLoaded', function() {
 
  // REPLACE WITH YOUR ACTUAL BASIN ENDPOINT URL
  const BASIN_ENDPOINT = 'https://usebasin.com/f/53bcf91d96ae';
  // REPLACE WITH YOUR IMGBB API KEY (free signup at api.imgbb.com)
  const IMGBB_API_KEY = '2d75eb571db440c72fc76ed61e30ab90';
  // Current artwork URLs for latest quote
  let currentArtworkUrls = [];

  // ────────────────────────────────────────────────
  // MULTI-ORDER DISCOUNT HELPERS
  // ────────────────────────────────────────────────
function calculateMarkup(baseCost) {
  if (baseCost < 144) return 50;
  if (baseCost < 350) return baseCost * 0.35;
  if (baseCost < 1000) return baseCost * 0.30;
  if (baseCost < 2500) return baseCost * 0.28;
  return baseCost * 0.25;
}

  function calculateMultiOrderDiscount(quotes) {
    if (quotes.length <= 1) {
      return { savings: 0, savingsPercent: 0, combinedTotalExc: 0 };
    }
    let grandBase = 0;
    let sumIndividualTotals = 0;
    quotes.forEach(quote => {
      grandBase += parseFloat(quote.baseCostWithFixed) || 0;
      sumIndividualTotals += parseFloat(quote.individualTotalExc) || 0;
    });
    const combinedMarkup = calculateMarkup(grandBase);
    const combinedTotalExc = Math.round((grandBase + combinedMarkup) * 100) / 100;
    const savings = Math.round((sumIndividualTotals - combinedTotalExc) * 100) / 100;
    const savingsPercent = sumIndividualTotals > 0 ? Math.round((savings / sumIndividualTotals) * 100) : 0;
    return {
      savings,
      savingsPercent,
      combinedTotalExc
    };
  }

  // Robust product image selector (ignores brand logo metafield)
function getProductImageUrl() {
  const selectors = [
    '.product-media__image',
    '.product__media img',
    'img[data-product-image]',
    '.product-featured-image img',
    '#panel-product-image img',
    '.product-media img'
  ];

  for (const selector of selectors) {
    const img = document.querySelector(selector);

    if (img && (img.currentSrc || img.src)) {
      const src = img.currentSrc || img.src;

      if (
        src.includes('brand-logo') ||
        img.classList.contains('brand-logo') ||
        img.closest('.brand-logo, .brand-logo-container')
      ) {
        continue;
      }

      return src;
    }
  }

  return '';
}

  // Upload to Imgbb (CORS allowed, works for images/PDF/AI/EPS)
  async function uploadFileToImgbb(file) {
    const formData = new FormData();
    formData.append('key', IMGBB_API_KEY);
    formData.append('image', file); // 'image' param works for any file type
    try {
      const response = await fetch('https://api.imgbb.com/1/upload', {
        method: 'POST',
        body: formData
      });
      const data = await response.json();
      if (data.success) {
        return data.data.url; // direct download URL (permanent)
      } else {
        console.error('Imgbb error:', data);
      }
    } catch (error) {
      console.error('Imgbb upload error:', error);
    }
    return null;
  }

  // Attach upload listener to all file inputs (initial + dynamic)
  function attachFileUploadListeners() {
    const fileInputs = document.querySelectorAll('input[name="attachment[]"]');
    fileInputs.forEach(input => {
      // Remove old listeners to avoid duplicates
      input.removeEventListener('change', handleFileChange);
      input.addEventListener('change', handleFileChange);
    });
  }

  // Replace the entire handleFileChange function with this
  async function handleFileChange() {
    // Collect files from ALL inputs (initial + any "Add another file")
    const allInputs = document.querySelectorAll('input[name="attachment[]"]');
    let allFiles = [];
    allInputs.forEach(input => {
      if (input.files && input.files.length > 0) {
        for (let file of input.files) {
          allFiles.push(file);
        }
      }
    });
    if (allFiles.length === 0) {
      currentArtworkUrls = [];
      const uploadStatus = document.getElementById('upload-status');
      if (uploadStatus) uploadStatus.style.display = 'none';
      return;
    }
    // Max 4 total
    if (allFiles.length > 4) {
      alert('Max 4 files total please');
      return;
    }
    const uploadStatus = document.getElementById('upload-status');
    if (uploadStatus) {
      uploadStatus.textContent = `Uploading ${allFiles.length} file(s)...`;
      uploadStatus.style.display = 'block';
    }
    currentArtworkUrls = []; // clear old
    let uploadedCount = 0;
    for (let file of allFiles) {
      const url = await uploadFileToImgbb(file);
      if (url) {
        currentArtworkUrls.push(url);
        uploadedCount++;
      }
    }
    if (uploadedCount > 0) {
      if (uploadStatus) uploadStatus.textContent = `Uploaded ${uploadedCount} of ${allFiles.length} file(s)!`;
    } else {
      if (uploadStatus) uploadStatus.textContent = 'Upload failed – try again.';
    }
  }

  // Add this at the very end of header.js (after everything else)
  setInterval(attachFileUploadListeners, 1000);

  // Get elements
  const decoSelect = document.getElementById('calc-decoration');
  const priceDisplay = document.getElementById('calc-price-display');
  const upsellDiv = document.getElementById('calc-upsell');

  if (!decoSelect || !priceDisplay || !upsellDiv) {
    console.warn("[Calculator] Core elements missing - skipping calculator (normal on non-product pages)");
  } else {
  }

  const mf = window.productMetafields || {};

  // ────────────────────────────────────────────────
  // SHARED DECORATION → METAFIELD KEY MAPPING
  // ────────────────────────────────────────────────
  const DECO_KEY_MAP = {
    '1-colour': '1_colour_print',
    '2-colour': '2_colour_print',
    '3-colour': '3_colour_print',
    '4-colour': '4_colour_print',
    'full-colour': 'full_colour_print',
    'embroidered': 'embroidery_cost',
    'laser-engraved': 'laser_engrave_costs'
  };

  // Dynamic quantity breaks (array of strings)
  const qtyBreaks = mf.quantity_breaks || [];

  // ────────────────────────────────────────────────
  // BOTH SIDES PRINTING – STATE VARIABLES
  // ────────────────────────────────────────────────
  let bothSidesEnabled = mf.custom?.print_both_sides === 'yes';
  let bothSidesChecked = false;
  let sameArtworkChecked = false;
  let umbrellaPanels = 'none';  // can be 'none', '2' or '4'

  // ────────────────────────────────────────────────
  // INIT BOTH SIDES CHECKBOXES (called once at start)
  // ────────────────────────────────────────────────
  function initBothSidesCheckboxes() {
    const container = document.getElementById('both-sides-container');
    if (!container) {
      console.warn("[BothSides] Container #both-sides-container not found in DOM");
      return;
    }

    if (!bothSidesEnabled) {
      container.style.display = 'none';
      return;
    }

    container.style.display = 'block';

    const bothCheckbox = document.getElementById('both-sides-checkbox');
    const sameWrapper = document.getElementById('same-artwork-wrapper');
    const sameCheckbox = document.getElementById('same-artwork-checkbox');

    if (!bothCheckbox || !sameWrapper || !sameCheckbox) {
      console.warn("[BothSides] One or more checkbox elements missing");
      return;
    }

    // Reset to defaults
    bothCheckbox.checked = false;
    sameCheckbox.checked = false;
    sameWrapper.style.display = 'none';

    // Listeners
    bothCheckbox.addEventListener('change', () => {
      bothSidesChecked = bothCheckbox.checked;
      sameWrapper.style.display = bothSidesChecked ? 'block' : 'none';
      if (!bothSidesChecked) {
        sameArtworkChecked = false;
        sameCheckbox.checked = false;
      }
      updatePrice();
    });

    sameCheckbox.addEventListener('change', () => {
      sameArtworkChecked = sameCheckbox.checked;
      updatePrice();
    });

  }

  function initUmbrellaPanels() {
  const container = document.getElementById('umbrella-panels-container');
  if (!container) return;

  if (mf.custom?.umbrella_panels !== 'yes') {
    container.style.display = 'none';
    return;
  }

  container.style.display = 'block';

  const cb2 = document.getElementById('panels-2-checkbox');
  const cb4 = document.getElementById('panels-4-checkbox');

  if (!cb2 || !cb4) return;

  cb2.checked = false;
  cb4.checked = false;
  umbrellaPanels = 'none';

  cb2.addEventListener('change', () => {
    if (cb2.checked) {
      cb4.checked = false;
      umbrellaPanels = '2';
    } else {
      umbrellaPanels = 'none';
    }
    updatePrice();
  });

  cb4.addEventListener('change', () => {
    if (cb4.checked) {
      cb2.checked = false;
      umbrellaPanels = '4';
    } else {
      umbrellaPanels = 'none';
    }
    updatePrice();
  });
}

  // Quantity number input + + / - buttons sync
  const minusBtn = document.getElementById('calc-quantity-minus');
  const plusBtn = document.getElementById('calc-quantity-plus');
  const numberInput = document.getElementById('calc-quantity-number');
  const minQtyDisplay = document.getElementById('min-qty-display');

  if (minusBtn && plusBtn && numberInput && minQtyDisplay) {
    let minQty = 1;
    let maxQty = 10000;
    if (qtyBreaks.length > 0) {
      minQty = parseInt(qtyBreaks[0]) || 1;
      maxQty = parseInt(qtyBreaks[qtyBreaks.length - 1]) || 10000;
    }
    const increment = parseInt(window.productIncrement) || 1;
    numberInput.min = minQty;
    numberInput.max = maxQty;
    numberInput.step = increment;
    numberInput.value = minQty;
    minQtyDisplay.textContent = minQty;

    function updateQuantity(newValue, isFinal = false) {
      if (newValue === '' || isNaN(parseInt(newValue))) return;
      let selectedQty = parseInt(newValue) || minQty;
      if (isFinal && increment > 1) {
        selectedQty = Math.round(selectedQty / increment) * increment;
      }
      if (isFinal) {
        selectedQty = Math.max(selectedQty, minQty);
        selectedQty = Math.min(selectedQty, maxQty);
      }
      numberInput.value = selectedQty;
      updatePrice();
      updateDecorationOptions();
    }

    minusBtn.addEventListener('click', () => {
      const current = parseInt(numberInput.value) || minQty;
      let newVal = current - increment;
      if (newVal < minQty) newVal = minQty;
      updateQuantity(newVal, true);
    });

    plusBtn.addEventListener('click', () => {
      const current = parseInt(numberInput.value) || minQty;
      let newVal = current + increment;
      if (newVal > maxQty) newVal = maxQty;
      updateQuantity(newVal, true);
    });

    numberInput.addEventListener('input', () => updateQuantity(numberInput.value, false));
    numberInput.addEventListener('blur', () => updateQuantity(numberInput.value, true));
    numberInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        updateQuantity(numberInput.value, true);
        numberInput.blur();
      }
    });

    updateQuantity(minQty, true);
  } else {
  }

  // Update quantity label with increment info
  const increment = parseInt(window.productIncrement) || 1;
  const incrementText = document.getElementById('increment-text');
  if (incrementText) {
    if (increment > 1) {
      incrementText.textContent = ` - Multiples of ${increment}`;
    } else {
      incrementText.textContent = '';
    }
  }

  function updateDecorationOptions() {
  if (!decoSelect) return;

  const selectedQty = parseInt(numberInput?.value) || 0;
  const options = decoSelect.options;
    let qtyIndex = qtyBreaks.findIndex(q => Number(q) === selectedQty);
    if (qtyIndex === -1) {
      let closestIndex = 0;
      for (let i = 0; i < qtyBreaks.length; i++) {
        if (Number(qtyBreaks[i]) <= selectedQty) {
          closestIndex = i;
        } else {
          break;
        }
      }
      qtyIndex = closestIndex;
    }

    const decoTypes = [
      { value: '1-colour', key: DECO_KEY_MAP['1-colour'] },
      { value: '2-colour', key: DECO_KEY_MAP['2-colour'] },
      { value: '3-colour', key: DECO_KEY_MAP['3-colour'] },
      { value: '4-colour', key: DECO_KEY_MAP['4-colour'] },
      { value: 'full-colour', key: DECO_KEY_MAP['full-colour'] },
      { value: 'embroidered', key: DECO_KEY_MAP['embroidered'] },
      { value: 'laser-engraved', key: DECO_KEY_MAP['laser-engraved'] }
    ];

    decoTypes.forEach(decoItem => {
      const option = Array.from(options).find(opt => opt.value === decoItem.value);
      if (!option) return;
      const priceArray = mf[decoItem.key] || [];
      const priceStr = (qtyIndex < priceArray.length) ? priceArray[qtyIndex] : null;
      const price = priceStr ? parseFloat(priceStr) : 0;
      const isAvailable = price > 0;
      option.disabled = !isAvailable;
      option.style.display = isAvailable ? '' : 'none';
    });

    if (decoSelect.value) {
      const selectedOption = decoSelect.options[decoSelect.selectedIndex];
      if (selectedOption && selectedOption.disabled) {
        decoSelect.value = '';
      }
    }

    updatePrice();

    if (window.innerWidth < 768) {
      for (let i = options.length - 1; i > 0; i--) {
        if (options[i].disabled) {
          decoSelect.removeChild(options[i]);
        }
      }
    }
  }

  function updatePrice() {
    if (!decoSelect || !decoSelect.value) return;
if (!numberInput) return;
    const qty = parseInt(numberInput?.value) || 0;
    const deco = decoSelect.value;

    if (qty === 0 || !deco) {
      priceDisplay.innerHTML = 'Please select quantity and decoration';
      upsellDiv.innerHTML = '';
      priceDisplay.classList.remove('has-content');
      return;
    }

    const mf = window.productMetafields || {};
    const qtyBreaks = mf.quantity_breaks || [];
    const basePrices = mf.base_prices || [];

    let qtyIndex = qtyBreaks.findIndex(q => Number(q) === qty);
    if (qtyIndex === -1) {
      let closestIndex = 0;
      for (let i = 0; i < qtyBreaks.length; i++) {
        if (Number(qtyBreaks[i]) <= qty) {
          closestIndex = i;
        } else {
          break;
        }
      }
      qtyIndex = closestIndex;
    }

    const base = (qtyIndex !== -1 && qtyIndex < basePrices.length) ? parseFloat(basePrices[qtyIndex]) || 0 : 0;

    const decoKey = DECO_KEY_MAP[deco] || (deco.replace('-', '_') + '_print');
    let addOn = (qtyIndex !== -1 && qtyIndex < (mf[decoKey]?.length || 0)) ? parseFloat(mf[decoKey][qtyIndex]) || 0 : 0;

    // Umbrella panels multiplication
if (umbrellaPanels === '2') {
  addOn *= 2;
} else if (umbrellaPanels === '4') {
  addOn *= 4;
}

    // ────────────────────────────────────────────────
    // BOTH SIDES PRINTING LOGIC – applied here
    // ────────────────────────────────────────────────
    let extraSetup = 0;
    const setupCosts = {
      '1-colour': parseFloat(mf['1_colour_set_up_cost'] || 0),
      '2-colour': parseFloat(mf['2_colour_set_up_cost'] || 0),
      '3-colour': parseFloat(mf['3_colour_set_up_cost'] || 0),
      '4-colour': parseFloat(mf['4_colour_set_up_cost'] || 0),
      'full-colour': parseFloat(mf['full_colour_set_up_cost'] || 0),
      'embroidered': parseFloat(mf['embroidery_set_up_cost'] || 0),
      'laser-engraved': parseFloat(mf['laser_set_up_cost'] || 0)
    };
    let setup = setupCosts[deco] || 0;

    if (bothSidesEnabled && bothSidesChecked && ['1-colour','2-colour','3-colour','4-colour','full-colour'].includes(deco)) {
      addOn *= 2;  // double unit decoration cost - already working perfectly

      // Double the setup fee for second side
      setup *= 2;  // ← this is the key change: double the setup itself

      if (sameArtworkChecked) {
        setup /= 2;  // revert to single setup if same artwork
      }

      // Optional: force clean money value (safe, no harm)
      setup = Number(setup.toFixed(2));
    }

// Hide decoration guidance text when both sides OR umbrella panels are selected
const guidanceEl = document.getElementById('decoration-guidance-text');
if (guidanceEl) {
  if (
    (bothSidesEnabled && bothSidesChecked) ||
    (umbrellaPanels === '2' || umbrellaPanels === '4')
  ) {
    guidanceEl.style.display = 'none';
  } else {
    guidanceEl.style.display = '';  // show it again if nothing selected
  }
}

    const unitPrice = base > 0 ? base + addOn : addOn;
    const baseCost = qty * unitPrice;

    const carriagePrices = mf.carriage || [];
    const carriage = (qtyIndex !== -1 && qtyIndex < carriagePrices.length) ? parseFloat(carriagePrices[qtyIndex]) || 0 : 0;

    const baseCostWithFixed = baseCost + setup + extraSetup + carriage;

    let markup = 0;
    if (baseCostWithFixed < 144) {
      markup = 50;
    } else if (baseCostWithFixed < 350) {
      markup = baseCostWithFixed * 0.35;
    } else if (baseCostWithFixed < 1000) {
      markup = baseCostWithFixed * 0.30;
    } else if (baseCostWithFixed < 2500) {
      markup = baseCostWithFixed * 0.28;
    } else {
      markup = baseCostWithFixed * 0.25;
    }

    const totalPrice = baseCostWithFixed + markup;

    const roundedUnitPrice = Math.round((totalPrice / qty) * 100) / 100;
    const cleanTotal = roundedUnitPrice * qty;

    // Save for panel + multi-order discount + both sides flags
    window.currentRoundedUnitPrice = roundedUnitPrice;
    window.currentQty = qty;
    window.currentLeadTime = window.productMetafields?.lead_time?.value || 'Not selected';
    if (Array.isArray(window.currentLeadTime)) window.currentLeadTime = window.currentLeadTime[0] || 'Not selected';

    window.currentBaseCostWithFixed = baseCostWithFixed;
    window.currentIndividualTotalExc = cleanTotal;
    window.currentBothSides = bothSidesChecked;
    window.currentSameArtwork = sameArtworkChecked;
    window.currentUmbrellaPanels = umbrellaPanels;

    // Apply VAT if toggled on
    let displayUnit = roundedUnitPrice;
    let displayTotal = cleanTotal;

    if (vatOn) {
      displayUnit = Math.round(roundedUnitPrice * 1.2 * 100) / 100;
      displayTotal = Math.round(cleanTotal * 1.2 * 100) / 100;
    }

    priceDisplay.innerHTML = `
      <div class="unit-price">Unit Price: £${displayUnit.toFixed(2)}</div>
      <div class="total-price">Total: £${displayTotal.toFixed(2)}</div>
      <div class="setup-line">INCLUDES SET-UP & DELIVERY*</div>
    `;

    priceDisplay.classList.add('has-content');

    const productName = document.querySelector('.calc-product-title')?.textContent?.trim() || 'custom product';
const encodedProductName = encodeURIComponent(productName);
const encodedDeco = encodeURIComponent(deco || 'selected');

    let upsellText = '';

    if (qtyBreaks.length > 0) {
      const highestBreak = Math.max(...qtyBreaks.map(Number));

      if (qty >= highestBreak) {
        upsellText = `Need more? <a href="mailto:sales@kaboom-branding.co.uk?subject=Quote%20for%20${qty}%2Bpcs%20${encodedProductName}&body=Hi%20team,%0A%0AI'd%20like%20a%20quote%20for%20${qty}%20pcs%20of%20${encodedProductName}%20with%20${encodedDeco}%20decoration.%0APlease%20send%20your%20best%20price!%0A%0AThanks!" style="color: #0066cc; text-decoration: underline; font-weight: bold;">Email sales@kaboom-branding.co.uk for our best price</a>`;
      } else {
        let nextQty = null;
        let nextIndex = -1;
        for (let i = 0; i < qtyBreaks.length; i++) {
          if (Number(qtyBreaks[i]) > qty) {
            nextQty = Number(qtyBreaks[i]);
            nextIndex = i;
            break;
          }
        }

        const safeBasePrices = Array.isArray(mf.base_prices) ? mf.base_prices : [];

        if (nextIndex !== -1 && (nextIndex < safeBasePrices.length || safeBasePrices.length === 0)) {
          const nextBase = parseFloat(safeBasePrices[nextIndex]) || 0;
          const nextDecoKey = DECO_KEY_MAP[deco] || (deco.replace('-', '_') + '_print');
          let nextAddOn = parseFloat(mf[nextDecoKey]?.[nextIndex] || 0);

          let nextExtraSetup = 0;
          if (bothSidesEnabled && bothSidesChecked) {
            nextAddOn *= 2;
            nextExtraSetup = setupCosts[deco] || 0;
            if (sameArtworkChecked) nextExtraSetup = 0;
          }

          const nextUnitPreFixed = nextBase + nextAddOn;
          const nextBaseCost = nextQty * nextUnitPreFixed;

          const nextSetup = setupCosts[deco] || 0;
          const nextCarriagePrices = mf.carriage || [];
          const nextCarriage = (nextIndex < nextCarriagePrices.length) ? parseFloat(nextCarriagePrices[nextIndex]) || 0 : 0;

          const nextBaseCostWithFixed = nextBaseCost + nextSetup + nextExtraSetup + nextCarriage;

          let nextMarkup = 0;
          if (nextBaseCostWithFixed < 144) {
            nextMarkup = 50;
          } else if (nextBaseCostWithFixed < 350) {
            nextMarkup = nextBaseCostWithFixed * 0.35;
          } else if (nextBaseCostWithFixed < 1000) {
            nextMarkup = nextBaseCostWithFixed * 0.30;
          } else if (nextBaseCostWithFixed < 2500) {
            nextMarkup = nextBaseCostWithFixed * 0.28;
          } else {
            nextMarkup = nextBaseCostWithFixed * 0.25;
          }

          const nextTotal = nextBaseCostWithFixed + nextMarkup;

          const roundedNextUnitPrice = Math.round((nextTotal / nextQty) * 100) / 100;
          const displayedNextUnitPrice = vatOn ? Math.round(roundedNextUnitPrice * 1.2 * 100) / 100 : roundedNextUnitPrice;

          const displayedUnitPrice = displayUnit;

          const unitSavings = displayedUnitPrice - displayedNextUnitPrice;
          const rawPercent = unitSavings > 0 ? (unitSavings / displayedUnitPrice) * 100 : 0;

          upsellText = `Upgrade to <strong>${nextQty}</strong> – only £${displayedNextUnitPrice.toFixed(2)} each – `;

          if (rawPercent > 0) {
            const percentDisplay = rawPercent < 1 ? rawPercent.toFixed(1) : Math.round(rawPercent);
            upsellText += `<span style="color: green; font-weight: bold;">save ${percentDisplay}%</span>`;
          } else {
            upsellText += `(even better deals on higher tiers!)`;
          }
        }
      }
    }

    upsellDiv.innerHTML = upsellText;

    showLeadtimeAndDelivery();
  }

  // Event listeners
  if (decoSelect) {
  decoSelect.addEventListener('change', updateDecorationOptions);
}

  const nextBtn = document.getElementById('next-proof-btn');
  if (nextBtn && numberInput && decoSelect) {
    function toggleNextButton() {
      const qtySelected = numberInput.value && numberInput.value !== '' && parseInt(numberInput.value) >= 1;
      const decoSelected = decoSelect.value !== '';
      nextBtn.style.display = (qtySelected && decoSelected) ? 'block' : 'none';
    }

    numberInput.addEventListener('input', toggleNextButton);
    numberInput.addEventListener('blur', toggleNextButton);
    decoSelect.addEventListener('change', toggleNextButton);

    toggleNextButton();
  }

  const proofPanel = document.getElementById('proof-panel');
  const overlay = document.getElementById('panel-overlay');
  const closeBtn = document.getElementById('close-panel');

  if (nextBtn && proofPanel && overlay) {
    nextBtn.addEventListener('click', function() {
      const selectedColour = document.getElementById('calc-colour')?.value;

      if (!selectedColour || selectedColour === '') {
        const warning = document.createElement('div');
        warning.className = 'warning-popup';
        warning.textContent = 'Please choose a product colour before proceeding';
        warning.style.cssText = `
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background:#ffffff;
          color: #33404f;
          padding: 20px 20px;
          border-radius: 12px;
          border : 1px solid #000;
          font-size: 18px;
          font-weight: bold;
          z-index: 100000;
          box-shadow: 0 10px 30px rgba(0,0,0,0.3);
          animation: fadeInOut 3s forwards;
          text-align: center;
        `;
        document.body.appendChild(warning);

        warning.offsetHeight; // trigger reflow
        warning.style.animation = 'fadeInOut 3s forwards';

        setTimeout(() => warning.remove(), 3000);
        return;
      }

      proofPanel.classList.add('open');
      overlay.classList.add('open');
      document.body.classList.add('panel-open');

      setTimeout(() => {

        // Reset accordion states every time panel opens
        const allDetails = proofPanel.querySelectorAll('details');
        allDetails.forEach(detail => {
          if (detail.id === 'upload-section') {
            detail.open = true;
          } else {
            detail.open = false;
          }
          detail.style.display = 'block'; // ensure visible again
        });

        // Edit Details button closes panel (back to calculator)
        const editSummaryBtn = document.getElementById('edit-summary');
        if (editSummaryBtn) {
          editSummaryBtn.addEventListener('click', () => {
            const proofPanel = document.getElementById('proof-panel');
            const overlay = document.getElementById('panel-overlay');
            if (proofPanel && overlay) {
              proofPanel.classList.remove('open');
              overlay.classList.remove('open');
              document.body.classList.remove('panel-open');
              if (window.innerWidth <= 768) {
                proofPanel.style.height = '0';
                proofPanel.style.maxHeight = '0';
              }
            }
          });
        }

        // Prefill customer details from localStorage if available
        const savedCustomer = JSON.parse(localStorage.getItem('quoteCustomerDetails') || '{}');
        if (savedCustomer.name || savedCustomer.email || savedCustomer.mobile) {
          if (savedCustomer.name) document.getElementById('customer-name').value = savedCustomer.name;
          if (savedCustomer.email) {
            document.getElementById('customer-email').value = savedCustomer.email;
            document.getElementById('confirm-email').value = savedCustomer.email;
          }
          if (savedCustomer.mobile) document.getElementById('customer-mobile').value = savedCustomer.mobile;
        } else {
        }

        // Fill summary fields
        const panelQty = document.getElementById('panel-qty');
        const panelColour = document.getElementById('panel-colour');
        const panelDeco = document.getElementById('panel-deco');
        const panelUnit = document.getElementById('panel-unit');
        const panelTotal = document.getElementById('panel-total');

        if (panelQty) panelQty.textContent = numberInput?.value ? numberInput.value + ' pcs' : 'Not selected';
        if (panelColour) panelColour.textContent = document.getElementById('calc-colour')?.value || 'Not selected';
        if (panelDeco) {
         let decoText = decoSelect.value ? decoSelect.options[decoSelect.selectedIndex].text : 'Not selected';

if (window.currentBothSides) {
  decoText += ' (both sides)';
  if (window.currentSameArtwork) {
    decoText += ' – same artwork';
  }
} else if (window.currentUmbrellaPanels && window.currentUmbrellaPanels !== 'none') {
  decoText += ` print to ${window.currentUmbrellaPanels} panels (same design)`;
} else if (window.decorationGuidance) {
  decoText += ` ${window.decorationGuidance}`;
}

panelDeco.textContent = decoText;
        }

        const unitFullText = document.querySelector('#calc-price-display .unit-price')?.textContent || 'N/A';
        const totalFullText = document.querySelector('#calc-price-display .total-price')?.textContent || 'N/A';

        if (panelUnit) panelUnit.textContent = unitFullText.replace(/^Unit Price:\s*/, '').trim();
        if (panelTotal) panelTotal.textContent = totalFullText.replace(/^Total:\s*/, '').trim();

        // VAT & Grand Total
        const panelVat = document.getElementById('panel-vat');
        const panelGrand = document.getElementById('panel-grand');

        const cleanTotal = window.currentRoundedUnitPrice * window.currentQty;
        const vatAmount = Math.round(cleanTotal * 0.2 * 100) / 100;
        const grandTotal = Math.round(cleanTotal * 1.2 * 100) / 100;

        if (panelVat) panelVat.textContent = '£' + vatAmount.toFixed(2);
        if (panelGrand) panelGrand.textContent = '£' + grandTotal.toFixed(2);

        // Leadtime & delivery
        const leadTimeRaw = window.productMetafields?.lead_time || ['Not selected'];
        const leadTime = Array.isArray(leadTimeRaw) ? leadTimeRaw[0] : leadTimeRaw;

        const panelLeadtime = document.getElementById('panel-leadtime');
        const panelDelivery = document.getElementById('panel-delivery');

        if (panelLeadtime) panelLeadtime.textContent = leadTime;

        if (panelDelivery) {
          panelDelivery.textContent = 'Calculating...';
          calculateDeliveryDate(leadTime).then(dateStr => {
            panelDelivery.textContent = dateStr;
          }).catch(() => {
            panelDelivery.textContent = 'Unavailable';
          });
        }

        // Force form visible, hide others
        const proofForm = document.getElementById('proof-form');
        if (proofForm) proofForm.style.display = 'block';

        const proofSuccess = document.getElementById('proof-success');
        if (proofSuccess) proofSuccess.style.display = 'none';

        // Summary header
        const summaryHeader = document.getElementById('summary-header');
        if (summaryHeader) {
          const qty = numberInput?.value || 'Not selected';
          const colourRaw = document.getElementById('calc-colour')?.value || '';
          const colour = colourRaw && colourRaw !== 'Not selected' ? colourRaw : '';

          const decoText = decoSelect.value ? decoSelect.options[decoSelect.selectedIndex].text : 'Not selected';

          let guidance = window.decorationGuidance || '';
          if (Array.isArray(guidance)) guidance = guidance[0] || '';
          guidance = String(guidance).trim();

          const productName = document.getElementById('panel-product')?.textContent || '{{ product.title }}';

          let summaryText = `Product Summary: ${qty} x`;
          if (colour) summaryText += ` ${colour}`;
          summaryText += ` ${productName} Branded in a ${decoText}`;

          if (guidance !== '') summaryText += ` ${guidance}`;
          summaryHeader.textContent = summaryText.trim();
        }

        // Re-attach upload listeners after panel open (for dynamic inputs)
        attachFileUploadListeners();
        setupNoArtworkToggle();

// Ready state checker (supports "No Artwork Yet" mode)
let readyInterval = setInterval(() => {
  if (!proofPanel.classList.contains('open')) {
    clearInterval(readyInterval);
    return;
  }
  window.updateReadyState();

const formSection = document.getElementById('form-section');

const uploadSection = document.getElementById('upload-section');
const uploadTitle = uploadSection?.querySelector('summary h4');

if (uploadSection && formSection.classList.contains('ready')) {
  uploadSection.classList.add('ready');

  if (uploadTitle) {
    uploadTitle.textContent = '✓ Branding Complete';
  }
}

if (uploadSection && !formSection.classList.contains('ready')) {
  uploadSection.classList.remove('ready');

  if (uploadTitle) {
    uploadTitle.textContent = 'Branding';
  }
}

if (formSection && formSection.classList.contains('ready') && !formSection.open) {
  formSection.open = true;

  setTimeout(() => {
    formSection.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }, 150);
}
}, 400);

        // Stop interval when panel closes
        const stopReadyCheck = () => clearInterval(readyInterval);
        closeBtn.addEventListener('click', stopReadyCheck);
        overlay.addEventListener('click', stopReadyCheck);

        // Auto-close "Add your logo" (and summary) when "Continue" is clicked
        const formSection = document.getElementById('form-section');
        if (formSection) {
          const continueSummary = formSection.querySelector('summary');
          if (continueSummary) {
            continueSummary.addEventListener('click', () => {
              const uploadSection = document.getElementById('upload-section');
              if (uploadSection && uploadSection.open) {
                uploadSection.open = false;
              }
              const summarySection = document.getElementById('summary-section');
              if (summarySection && summarySection.open) {
                summarySection.open = false;
              }
            });
          }
        }

        proofPanel.style.display = 'block';
        void proofPanel.offsetWidth;
        proofPanel.style.display = '';
        proofPanel.style.height = '100%';
        proofPanel.style.maxHeight = '100vh';
      }, 50);
    });
  }

  if (closeBtn) {
    closeBtn.addEventListener('click', function() {
      proofPanel.classList.remove('open');
      overlay.classList.remove('open');
      document.body.classList.remove('panel-open');
      if (window.innerWidth <= 768) {
        proofPanel.style.height = '0';
        proofPanel.style.maxHeight = '0';
      }
    });
  }

  if (overlay) {
    overlay.addEventListener('click', function() {
      proofPanel.classList.remove('open');
      overlay.classList.remove('open');
      document.body.classList.remove('panel-open');
      if (window.innerWidth <= 768) {
        proofPanel.style.height = '0';
        proofPanel.style.maxHeight = '0';
      }

    });
  }

  // ─────────────────────────────────────────────────────────────
  // Two-step proof submission with confirmation
  // ─────────────────────────────────────────────────────────────

  const proofForm = document.getElementById('proof-form');
  const proofSuccess = document.getElementById('proof-success');
  const closeAfterSuccess = document.getElementById('close-after-success');


  // Attach confirmation button listeners
  function attachConfirmationListeners() {
    const addToQuoteBasketBtn = document.getElementById('add-to-quote-basket-btn');
    const getQuoteNowBtn = document.getElementById('get-quote-now-btn');
    if (addToQuoteBasketBtn) {
      addToQuoteBasketBtn.replaceWith(addToQuoteBasketBtn.cloneNode(true));
      document.getElementById('add-to-quote-basket-btn').addEventListener('click', async function() {

        const name = document.getElementById('customer-name')?.value.trim();
const email = document.getElementById('customer-email')?.value.trim();
const confirmEmail = document.getElementById('confirm-email')?.value.trim();

if (!name) {
  alert('Please enter your full name.');
  return;
}

if (!email || !confirmEmail) {
  alert('Please enter and confirm your email.');
  return;
}

if (email !== confirmEmail) {
  alert('Emails do not match.');
  return;
}

        const customerData = {
          name: document.getElementById('customer-name')?.value.trim() || '',
          email: document.getElementById('customer-email')?.value.trim() || '',
          mobile: document.getElementById('customer-mobile')?.value.trim() || ''
        };

        if (customerData.name || customerData.email || customerData.mobile) {
          localStorage.setItem('quoteCustomerDetails', JSON.stringify(customerData));
        }

        const leadTimeRaw = window.productMetafields?.lead_time || ['Not selected'];
        const leadTime = Array.isArray(leadTimeRaw) ? leadTimeRaw[0] : leadTimeRaw;

        let deliveryDate = 'Not available';
        try {
          deliveryDate = await calculateDeliveryDate(leadTime);
        } catch (e) {
          console.warn('Delivery calc failed on add', e);
        }

        const quote = {
          product: document.getElementById('panel-product')?.textContent || '{{ product.title }}',
          colour: document.getElementById('panel-colour')?.textContent || 'Not selected',
          decoration: document.getElementById('panel-deco')?.textContent || 'Not selected',
          text: document.getElementById('logo-text')?.value.trim() || 'No text provided',
          leadtimeRaw: leadTimeRaw,
          quantity: numberInput?.value || 'Not selected',
          unitCost: document.getElementById('panel-unit')?.textContent?.replace('Unit Price: ', '').trim() || 'N/A',
          totalExc: document.getElementById('panel-total')?.textContent?.replace('Total: ', '').trim() || 'N/A',
          vat: document.getElementById('panel-vat')?.textContent || '£0.00',
          totalInc: document.getElementById('panel-grand')?.textContent || '£0.00',
          customerName: customerData.name,
          customerEmail: customerData.email,
          customerMobile: customerData.mobile,
          imageUrl: getProductImageUrl(),
          artworkUrls: currentArtworkUrls.length > 0 ? currentArtworkUrls : ['No files uploaded'],
          productHandle: window.product?.handle || window.location.pathname.split('/products/')[1]?.split('?')[0] || '',
decorationValue: decoSelect?.value || '',
minQty: parseInt(qtyBreaks?.[0], 10) || parseInt(numberInput?.min, 10) || 1,
maxQty: parseInt(qtyBreaks?.[qtyBreaks.length - 1], 10) || parseInt(numberInput?.max, 10) || 10000,
increment: parseInt(window.productIncrement, 10) || parseInt(numberInput?.step, 10) || 1,
          baseCostWithFixed: window.currentBaseCostWithFixed || 0,
          individualTotalExc: window.currentIndividualTotalExc || 0,
          bothSides: window.currentBothSides,
          sameArtwork: window.currentSameArtwork,
          umbrellaPanels: umbrellaPanels
        };

        addToQuoteBasket(quote);

setTimeout(() => {

  const basketPanel = document.getElementById('quote-basket-panel');
  const basketOverlay = document.getElementById('quote-basket-overlay');

  if (basketPanel) {
    basketPanel.classList.add('open');
    basketPanel.style.display = 'block';
    basketPanel.style.right = '0px';
    basketPanel.style.transform = 'translateX(0)';
  }

  if (basketOverlay) {
    basketOverlay.style.display = 'block';
  }

}, 300);

        const toast = document.createElement('div');
        toast.style.cssText = `position:fixed;bottom:30px;right:30px;background:#83b9eb;color:white;padding:16px 24px;border-radius:8px;box-shadow:0 4px 20px rgba(0,0,0,0.3);z-index:9999;font-size:16px;max-width:420px;opacity:0;transition:opacity 0.5s;`;
        toast.innerHTML = `Quote added to basket!<br>View basket or add more.`;
        document.body.appendChild(toast);
        setTimeout(() => toast.style.opacity = '1', 100);
        setTimeout(() => { toast.style.opacity = '0'; setTimeout(() => toast.remove(), 600); }, 6000);

        resetPanelAndForm();
        currentArtworkUrls = [];
      });
    }

    if (getQuoteNowBtn) {
      getQuoteNowBtn.replaceWith(getQuoteNowBtn.cloneNode(true));
      const newJustProofBtn = document.getElementById('get-quote-now-btn');
      newJustProofBtn.addEventListener('click', async function() {

        const name = document.getElementById('customer-name')?.value.trim();
const email = document.getElementById('customer-email')?.value.trim();
const confirmEmail = document.getElementById('confirm-email')?.value.trim();

if (!name) {
  alert('Please enter your full name.');
  return;
}

if (!email || !confirmEmail) {
  alert('Please enter and confirm your email.');
  return;
}

if (email !== confirmEmail) {
  alert('Emails do not match.');
  return;
}

        newJustProofBtn.disabled = true;
        newJustProofBtn.textContent = 'Submitting...';

        try {
  // Build the current quote from proof panel
  const quote = {
    product: document.getElementById('panel-product')?.textContent || '{{ product.title }}',
    colour: document.getElementById('panel-colour')?.textContent || 'Not selected',
    decoration: document.getElementById('panel-deco')?.textContent || 'Not selected',
    text: document.getElementById('logo-text')?.value.trim() || 'No text provided',
    leadtimeRaw: window.productMetafields?.lead_time || ['Not selected'],  // safe default
    quantity: numberInput?.value || 'Not selected',
    unitCost: document.getElementById('panel-unit')?.textContent?.replace('Unit Price: ', '').trim() || 'N/A',
    totalExc: document.getElementById('panel-total')?.textContent?.replace('Total: ', '').trim() || 'N/A',
    vat: document.getElementById('panel-vat')?.textContent || '£0.00',
    totalInc: document.getElementById('panel-grand')?.textContent || '£0.00',
    customerName: document.getElementById('customer-name')?.value.trim() || 'Not provided',
    customerEmail: document.getElementById('customer-email')?.value.trim() || 'Not provided',
    customerMobile: document.getElementById('customer-mobile')?.value.trim() || 'Not provided',
    imageUrl: getProductImageUrl(),
    artworkUrls: currentArtworkUrls.length > 0 ? currentArtworkUrls : ['No files uploaded'],
    productHandle: window.product?.handle || window.location.pathname.split('/products/')[1]?.split('?')[0] || '',
decorationValue: decoSelect?.value || '',
minQty: parseInt(qtyBreaks?.[0], 10) || parseInt(numberInput?.min, 10) || 1,
maxQty: parseInt(qtyBreaks?.[qtyBreaks.length - 1], 10) || parseInt(numberInput?.max, 10) || 10000,
increment: parseInt(window.productIncrement, 10) || parseInt(numberInput?.step, 10) || 1,
    baseCostWithFixed: window.currentBaseCostWithFixed || 0,
    individualTotalExc: window.currentIndividualTotalExc || 0,
    bothSides: window.currentBothSides,
    sameArtwork: window.currentSameArtwork,
    umbrellaPanels: umbrellaPanels
  };

  // Automatically add the current quote to basket
  addToQuoteBasket(quote);

  // Small delay to ensure localStorage syncs
  await new Promise(resolve => setTimeout(resolve, 500));

  // Now read the updated basket
  const quotes = JSON.parse(localStorage.getItem('quoteBasket') || '[]');

  if (quotes.length === 0) {
    alert('No quotes to submit.');
    newJustProofBtn.disabled = false;
    newJustProofBtn.textContent = 'No thanks – just get on with my proofs';
    return;
  }

  const savedCustomer = JSON.parse(localStorage.getItem('quoteCustomerDetails') || '{}');

  const customerName = document.getElementById('customer-name')?.value.trim() || 'Not provided';
  const customerEmail = document.getElementById('customer-email')?.value.trim() || 'Not provided';
  const customerMobile = document.getElementById('customer-mobile')?.value.trim() || 'Not provided';

  const now = new Date();
  let orderDate = new Date(now);
  const hour = orderDate.getHours();
  if (hour >= 15) {
    orderDate.setDate(orderDate.getDate() + 1);
  }
  while (orderDate.getDay() === 0 || orderDate.getDay() === 6) {
    orderDate.setDate(orderDate.getDate() + 1);
  }
  const orderByDate = orderDate.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short'
  });
  const deliveryCondition = `(if ordered and artwork approved before 3PM on ${orderByDate})`;

  const freshDeliveryDates = [];
  for (const quote of quotes) {
    let leadTimeRaw = quote.leadtimeRaw || ['Not selected'];
    const leadTime = Array.isArray(leadTimeRaw) ? leadTimeRaw[0] : leadTimeRaw;
    let freshDate = 'Not available';
    try {
      freshDate = await calculateDeliveryDate(leadTime);
    } catch (e) {
      console.warn('Delivery recalc failed on submit', e);
    }
    freshDeliveryDates.push(freshDate);
  }

  const formData = new FormData();
  formData.append('01_Customer Name', customerName);
  formData.append('02_Customer Email', customerEmail);
  formData.append('03_Customer Number', customerMobile);

  let quoteSummary = `Multi-Quote Request (${quotes.length} items)\n\n`;
  quotes.forEach((quote, index) => {
    quoteSummary += `Quote ${index + 1}:\n`;
    quoteSummary += `Quantity: ${quote.quantity}\n`;
    quoteSummary += `Colour: ${quote.colour}\n`;
    quoteSummary += `Product: ${quote.product}\n`;
    let decoSummary = quote.decoration.replace(/print to 1 panel/gi, '').trim();

// Only add panel text if it's not already in decoSummary
if (quote.umbrellaPanels && quote.umbrellaPanels !== 'none') {
  if (!decoSummary.includes(`print to ${quote.umbrellaPanels} panels (same design)`)) {
    decoSummary += ` print to ${quote.umbrellaPanels} panels (same design)`;
  }
}
quoteSummary += `Decoration: ${decoSummary}\n`;

    if (quote.bothSides) {
      quoteSummary += `Both sides printing: Yes`;
      if (quote.sameArtwork) quoteSummary += ' (same artwork)';
      quoteSummary += '\n';
    }
    quoteSummary += `Lead time: ${quote.leadtimeRaw ? (Array.isArray(quote.leadtimeRaw) ? quote.leadtimeRaw[0] : quote.leadtimeRaw) : 'Not selected'}\n`;
    quoteSummary += `Estimated Delivery: ${freshDeliveryDates[index]} ${deliveryCondition}\n`;
    quoteSummary += `Total exc VAT: ${quote.totalExc}\n`;
    quoteSummary += `Total inc VAT: ${quote.totalInc}\n`;
    quoteSummary += `Text / Logo: ${quote.text}\n`;
    quoteSummary += `Artwork URLs:\n${Array.isArray(quote.artworkUrls) ? quote.artworkUrls.join('\n') : 'No files uploaded'}\n\n`;
  });
  quoteSummary += `Note: URLs are direct downloads – click to save files.`;
  formData.append('14_Quote Details', quoteSummary);

  const discount = calculateMultiOrderDiscount(quotes);
  formData.append('15_Multi_Order_Saving', discount.savings > 0 ? `£${discount.savings.toFixed(2)} (${discount.savingsPercent}%)` : '0.00');
  formData.append('16_Combined_Total_Exc_VAT', `£${discount.combinedTotalExc.toFixed(2)}`);

  const response = await fetch(BASIN_ENDPOINT, {
    method: 'POST',
    body: formData
  });

  if (response.ok) {

  if (typeof fbq !== 'undefined') {
    fbq('trackCustom', 'SubmitQuote');
  }

  document.querySelectorAll('#proof-panel details').forEach(detail => detail.style.display = 'none');
  document.getElementById('proof-success').style.display = 'block';

} else {
  alert('Submission failed – check console.');
}
} catch (error) {
  console.error('Basin submit error:', error);
  alert('Error submitting. Please try again.');
} finally {
  const basketPanel = document.getElementById('quote-basket-panel');
  const basketOverlay = document.getElementById('quote-basket-overlay');
  if (basketPanel && basketOverlay) {
    basketPanel.classList.remove('open');
    basketOverlay.style.display = 'none';
    basketPanel.style.display = 'none';
  }
  localStorage.removeItem('quoteBasket');
  localStorage.removeItem('quoteCustomerDetails');
  renderQuoteBasket();
  updateBasketCount();
  newJustProofBtn.disabled = false;
  newJustProofBtn.textContent = 'Get My Quote Now';
}
      });
    }
  }

  attachConfirmationListeners();

  // Custom Quote Basket (localStorage-based)
function addToQuoteBasket(quoteData) {
  let quotes = JSON.parse(localStorage.getItem('quoteBasket') || '[]');
  quotes.push(quoteData);
  localStorage.setItem('quoteBasket', JSON.stringify(quotes));

  // === Meta Pixel: AddToQuote ===
  if (typeof fbq !== 'undefined') {
    fbq('trackCustom', 'AddToQuoteBasket', {
      content_name: quoteData.product || '',
      content_id: quoteData.productHandle || '',
      content_type: 'product',
      quantity: parseInt(quoteData.quantity) || 1,
      colour: quoteData.colour || '',
      decoration: quoteData.decoration || '',
      currency: 'GBP',
      value: 0
    });
  }
  // === End Meta Pixel ===

  updateBasketCount();
  renderQuoteBasket();
}

  function renderQuoteBasket() {
    const list = document.getElementById('quote-list');
    if (!list) return;

    const quotes = JSON.parse(localStorage.getItem('quoteBasket') || '[]');

    // Multi-Order Discount Banner
    const discount = calculateMultiOrderDiscount(quotes);
    const banner = document.getElementById('multi-discount-banner');

    if (banner) {
      if (discount.savings > 0) {
        banner.innerHTML = `
          <div style="background: #33404f; padding: 18px; border-radius: 12px; text-align: center; margin-bottom: 20px; box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);">
            <strong style="font-size: 18px; color: #7dc9c7;; display: inline-flex; align-items: center; gap: 8px;">
  <img 
    src="https://cdn.shopify.com/s/files/1/0979/0236/2999/files/basket-volume.png?v=1779313240" 
    alt="Multi Order Discount" 
    style="height: 31px; width: auto; display: block;"
  >
  MULTI-PRODUCT-DISCOUNT UNLOCKED
</strong><br>
            <span class="quote-percentage-saved" style="font-size: 33px; font-weight: bold; color: #ffffff;">
              SAVE £${discount.savings.toFixed(2)} (${discount.savingsPercent}%)
            </span><br>
            <span class="quote-enhanced" style="font-size: 18px; color: #ffffff; font-weight: 600;">
              Enhanced Volume Discount Applied
            </span><br><br>
            <small style="color: #ffffff; font-size: 18px;">
              New combined total (exc VAT): £${discount.combinedTotalExc.toFixed(2)}
            </small>
          </div>`;
      } else {
        let message = '';
        if (quotes.length === 0) {
          message = 'Add your first quote to start building your basket!';
        } else if (quotes.length === 1) {
          message = 'Add one more quote to unlock multi-order savings!';
        } else {
          message = 'Add even more quotes to increase your savings further!';
        }

        banner.innerHTML = `
          <div style="background: #33404f; padding:16px; border-radius:10px; text-align:center; color:#ffffff; font-size:17px; margin-bottom: 20px; border: 3px solid #bfaa6f;">
            ${message}
            <br><small style="opacity:0.8;">The more you add, the bigger the discount!</small>
          </div>`;
      }
    }

    list.innerHTML = '';

    if (quotes.length === 0) {
      list.innerHTML = '<p style="text-align:center; color:#999;">No quotes in basket yet</p>';
      return;
    }

    quotes.forEach((quote, index) => {
  const attachedUrls = Array.isArray(quote.artworkUrls) && quote.artworkUrls.length > 0
    ? quote.artworkUrls.join(', ')
    : 'No files uploaded';

  const item = document.createElement('div');
  item.className = 'quote-item';
  item.style.cssText = 'padding: 15px; border-bottom: 1px solid #eee; background: #ffffff; border-radius: 8px; margin-bottom: 10px; box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);';
  item.innerHTML = `
  <div class="quote-item-top">
    <div class="quote-item-image-wrap">
      <img src="${quote.imageUrl || 'https://placehold.co/120x120/e5e5e5/666?text=No+Image'}" 
     alt="${quote.product}" 
     class="quote-item-image"
     onerror="this.src='https://placehold.co/120x120/e5e5e5/666?text=No+Image'">
    </div>

    <div class="quote-item-main">
      <h4 class="quote-item-title">${quote.product}</h4>

      <div class="quote-qty-block">
        <strong>Quantity:</strong>

        <div class="quote-qty-controls">
          <button type="button" class="quote-qty-minus" data-index="${index}">−</button>

          <input
            type="number"
            class="quote-qty-input"
            data-index="${index}"
            min="${quote.minQty || 1}"
            max="${quote.maxQty || 10000}"
            step="${quote.increment || 1}"
            value="${parseInt(quote.quantity, 10) || 1}"
          >

          <button type="button" class="quote-qty-plus" data-index="${index}">+</button>

          <button type="button" class="quote-qty-update" data-index="${index}">
            Update
          </button>
        </div>

        <small>
          Min ${quote.minQty || 1}${quote.maxQty ? ` / Max ${quote.maxQty}` : ''}${quote.increment && quote.increment > 1 ? ` / Multiples of ${quote.increment}` : ''}
        </small>
      </div>
    </div>
  </div>

  <div class="quote-item-details">
          <p><strong>Colour:</strong> ${quote.colour}</p>
<p><strong>Decoration:</strong> ${quote.decoration.replace(/print to 1 (side|panel)/gi, '').trim()}</p>
          <p><strong>Text/Logo:</strong> ${quote.text}</p>
          <p><strong>Lead time:</strong> ${quote.leadtimeRaw ? (Array.isArray(quote.leadtimeRaw) ? quote.leadtimeRaw[0] : quote.leadtimeRaw) : 'Not selected'}</p>
         <p><strong>Estimated delivery:</strong> 
  <span class="fresh-delivery" data-leadtime='${JSON.stringify(quote.leadtimeRaw || ["Not selected"])}'>
    ${quote.deliveryDate || quote.estimatedDelivery || 'Calculating...'}
  </span>
</p>
          <p><strong>Artwork URLs:</strong> ${attachedUrls}</p>
          <p><strong>Unit Price:</strong> ${quote.unitCost}</p>
          <p><strong>Total exc VAT:</strong> ${quote.totalExc}</p>
          <p style="font-size:20px;"><strong>Grand Total: </strong>${quote.totalInc}</p>
          <p><strong>Client:</strong> ${quote.customerName} | ${quote.customerEmail} | ${quote.customerMobile}</p>
          <button 
  type="button"
  class="quote-remove-btn"
  data-index="${index}" 
  style="margin-top: 10px; background: #33404f; color: white; border: none; padding: 8px 16px; border-radius: 6px; cursor: pointer; display: inline-flex; align-items: center; gap: 8px;"
>
  <!-- Inline SVG trash icon – 21px height, white stroke -->
  <svg 
    width="21" 
    height="21" 
    viewBox="0 0 24 24" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
    style="flex-shrink: 0;"
  >
    <path 
      d="M3 6H21M19 6V20C19 21.1046 18.1046 22 17 22H7C5.89543 22 5 21.1046 5 20V6M8 6V4C8 2.89543 8.89543 2 10 2H14C15.1046 2 16 2.89543 16 4V6M10 11V17M14 11V17" 
      stroke="white" 
      stroke-width="2" 
      stroke-linecap="round" 
      stroke-linejoin="round"
    />
  </svg>
  REMOVE
</button>
        </div></div>
      `;
      list.appendChild(item);

      const freshSpan = item.querySelector('.fresh-delivery');
      if (freshSpan) {
        let leadTimeRaw;
        try {
          leadTimeRaw = JSON.parse(freshSpan.dataset.leadtime || '["Not selected"]');
        } catch (e) {
          leadTimeRaw = ['Not selected'];
        }
        const leadTime = Array.isArray(leadTimeRaw) ? leadTimeRaw[0] : leadTimeRaw;

        calculateDeliveryDate(leadTime)
          .then(dateStr => {
            freshSpan.textContent = dateStr;
          })
          .catch(() => {
            freshSpan.textContent = 'Unavailable';
          });
      }
    });
  }

  window.removeQuote = function(index) {
    let quotes = JSON.parse(localStorage.getItem('quoteBasket') || '[]');
    quotes.splice(index, 1);
    localStorage.setItem('quoteBasket', JSON.stringify(quotes));
    renderQuoteBasket();
    updateBasketCount();
  };

  document.addEventListener('click', function(e) {
  const removeBtn = e.target.closest('.quote-remove-btn');
  if (!removeBtn) return;

  e.preventDefault();
  e.stopPropagation();

  const index = parseInt(removeBtn.dataset.index, 10);
  if (isNaN(index)) return;

  let quotes = JSON.parse(localStorage.getItem('quoteBasket') || '[]');

  quotes.splice(index, 1);

  localStorage.setItem('quoteBasket', JSON.stringify(quotes));

  requestAnimationFrame(() => {
    renderQuoteBasket();
    updateBasketCount();
  });

  setTimeout(() => {
    renderQuoteBasket();
    updateBasketCount();
  }, 100);
});

  document.addEventListener('click', function(e) {
  const minus = e.target.closest('.quote-qty-minus');
  const plus = e.target.closest('.quote-qty-plus');
  const update = e.target.closest('.quote-qty-update');

  if (!minus && !plus && !update) return;

  const button = minus || plus || update;
  const index = parseInt(button.dataset.index, 10);
  const input = document.querySelector(`.quote-qty-input[data-index="${index}"]`);

  if (!input) return;

  const min = parseInt(input.min, 10) || 1;
  const max = parseInt(input.max, 10) || 10000;
  const step = parseInt(input.step, 10) || 1;

  let value = parseInt(input.value, 10) || min;

  if (minus) value -= step;
  if (plus) value += step;

  value = Math.max(min, value);
  value = Math.min(max, value);
  value = Math.ceil(value / step) * step;
  if (value > max) value = max;

  input.value = value;

  if (update) {
    const quotes = JSON.parse(localStorage.getItem('quoteBasket') || '[]');
    const quote = quotes[index];

    if (!quote || !quote.productHandle) {
      alert('Sorry, this quote cannot be recalculated because the product link is missing.');
      return;
    }

    localStorage.setItem('kaboomQuoteUpdateRequest', JSON.stringify({
      quoteIndex: index,
      newQuantity: value,
      quote: quote
    }));

    window.location.href = `/products/${quote.productHandle}?kquoteUpdate=1`;
  }
});

  function updateBasketCount() {
    const countEl = document.getElementById('quote-count');
    if (!countEl) return;
    const quotes = JSON.parse(localStorage.getItem('quoteBasket') || '[]');
    const count = quotes.length;
    countEl.textContent = count;
    countEl.style.display = count > 0 ? 'block' : 'none';
  }

  // Basket open/close logic + delegation fix + bubble safety
  window.addEventListener('load', () => {

    const basketPanel = document.getElementById('quote-basket-panel');
    const basketOverlay = document.getElementById('quote-basket-overlay');
    const closeBtn = document.getElementById('close-quote-basket');
    const submitBtn = document.getElementById('submit-quotes');

    // Delegation listener – bypasses theme click blocking
    document.addEventListener('click', (e) => {
      const toggle = e.target.closest('#quote-basket-toggle');
      if (toggle) {
        e.preventDefault();
        e.stopPropagation();
        renderQuoteBasket();                    // immediate call
        setTimeout(() => renderQuoteBasket(), 0);   // micro-delay to ensure DOM is ready
        setTimeout(() => renderQuoteBasket(), 100); // extra safety delay (covers most timing glitches)
        if (basketPanel && basketOverlay) {
          basketPanel.style.display = 'block';
          basketOverlay.style.display = 'block';
          basketPanel.classList.add('open');
        }
      }
    });


    // Close button listener (unchanged)
    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (basketPanel && basketOverlay) {
          basketPanel.classList.remove('open');
          basketOverlay.style.display = 'none';
          basketPanel.style.display = 'none';
        }
      });
    }

    // Overlay listener (unchanged)
    if (basketOverlay) {
      basketOverlay.addEventListener('click', (e) => {
        e.preventDefault();
        if (basketPanel) {
          basketPanel.classList.remove('open');
          basketPanel.style.display = 'none';
        }
        basketOverlay.style.display = 'none';
      });
    }

    // Submit button listener
    if (submitBtn) {
      submitBtn.replaceWith(submitBtn.cloneNode(true));  // Removes any old listeners
      const newSubmitBtn = document.getElementById('submit-quotes');
      newSubmitBtn.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();


        if (newSubmitBtn.disabled) return;

        newSubmitBtn.disabled = true;
        newSubmitBtn.textContent = 'Submitting...';

        try {
          const quotes = JSON.parse(localStorage.getItem('quoteBasket') || '[]');
          const savedCustomer = JSON.parse(localStorage.getItem('quoteCustomerDetails') || '{}');

          const customerName = savedCustomer.name || 'Not provided';
          const customerEmail = savedCustomer.email || 'Not provided';
          const customerMobile = savedCustomer.mobile || 'Not provided';

          const now = new Date();
          let orderDate = new Date(now);
          const hour = orderDate.getHours();
          if (hour >= 15) {
            orderDate.setDate(orderDate.getDate() + 1);
          }
          while (orderDate.getDay() === 0 || orderDate.getDay() === 6) {
            orderDate.setDate(orderDate.getDate() + 1);
          }
          const orderByDate = orderDate.toLocaleDateString('en-GB', {
            weekday: 'short',
            day: 'numeric',
            month: 'short'
          });
          const deliveryCondition = `(if ordered and artwork approved before 3PM on ${orderByDate})`;

          // CHANGED: Re-calculate fresh delivery dates right before sending to Basin
          const freshDeliveryDates = [];
          for (const quote of quotes) {
            let leadTimeRaw = quote.leadtimeRaw || ['Not selected'];
            const leadTime = Array.isArray(leadTimeRaw) ? leadTimeRaw[0] : leadTimeRaw;

            let freshDate = 'Not available';
            try {
              freshDate = await calculateDeliveryDate(leadTime);
            } catch (e) {
              console.warn('Delivery recalc failed on submit', e);
            }
            freshDeliveryDates.push(freshDate);
          }

          if (quotes.length === 0) {
            alert('No quotes to submit.');
            newSubmitBtn.disabled = false;
            newSubmitBtn.textContent = 'Submit All for Proofs';
            return;
          }

          const formData = new FormData();

          formData.append('01_Customer Name', customerName);
          formData.append('02_Customer Email', customerEmail);
          formData.append('03_Customer Number', customerMobile);

          let quoteSummary = `Multi-Quote Request (${quotes.length} items)\n\n`;
          quotes.forEach((quote, index) => {
            quoteSummary += `Quote ${index + 1}:\n`;
            quoteSummary += `Quantity: ${quote.quantity}\n`;
            quoteSummary += `Colour: ${quote.colour}\n`;
            quoteSummary += `Product: ${quote.product}\n`;
            quoteSummary += `Decoration: ${quote.decoration}\n`;
            quoteSummary += `Lead time: ${quote.leadtimeRaw ? (Array.isArray(quote.leadtimeRaw) ? quote.leadtimeRaw[0] : quote.leadtimeRaw) : 'Not selected'}\n`;
            quoteSummary += `Estimated Delivery: ${freshDeliveryDates[index]} ${deliveryCondition}\n`;
            quoteSummary += `Total exc VAT: ${quote.totalExc}\n`;
            quoteSummary += `Total inc VAT: ${quote.totalInc}\n`;
            quoteSummary += `Text / Logo: ${quote.text}\n`;
            quoteSummary += `Artwork URLs:\n${Array.isArray(quote.artworkUrls) ? quote.artworkUrls.join('\n') : 'No files uploaded'}\n\n`;
          });

          quoteSummary += `Note: URLs are direct downloads – click to save files.`;

          formData.append('14_Quote Details', quoteSummary);

const discount = calculateMultiOrderDiscount(quotes);

formData.append(
  '15_Multi_Order_Saving',
  discount.savings > 0
    ? `£${discount.savings.toFixed(2)} (${discount.savingsPercent}%)`
    : '£0.00 (0%)'
);

formData.append(
  '16_Combined_Total_Exc_VAT',
  `£${discount.combinedTotalExc.toFixed(2)}`
);

const response = await fetch(BASIN_ENDPOINT, {
  method: 'POST',
  body: formData
});
          if (response.ok) {

  if (typeof fbq !== 'undefined') {
    fbq('trackCustom', 'SubmitQuoteBasket');
  }

  // Close basket panel (your existing code)
  if (basketPanel && basketOverlay) {
    basketPanel.classList.remove('open');
    basketOverlay.style.display = 'none';
    basketPanel.style.display = 'none';
  }

  // Show basket-specific success
  const basketSuccess = document.getElementById('basket-success');
  if (basketSuccess) {
    basketSuccess.style.display = 'block';
  }
} else {
  alert('Submission failed – check console.');
}
        } catch (error) {
          console.error('Basin submit error:', error);
          alert('Error submitting. Please try again.');
        } finally {
          localStorage.removeItem('quoteBasket');
          localStorage.removeItem('quoteCustomerDetails');
          renderQuoteBasket();
          updateBasketCount();

          newSubmitBtn.disabled = false;
          newSubmitBtn.textContent = 'Submit All for Proofs';

          if (basketPanel && basketOverlay) {
            basketPanel.classList.remove('open');
            basketOverlay.style.display = 'none';
            basketPanel.style.display = 'none';
          }
        }
      });
    }

    // Initial render/count + extra safety to beat flash
    renderQuoteBasket();
    updateBasketCount();
    setTimeout(updateBasketCount, 1000);
    setTimeout(updateBasketCount, 2000);
    setInterval(updateBasketCount, 1000);

    const basketOpenParams = new URLSearchParams(window.location.search);

if (basketOpenParams.get('basketOpen') === '1') {

  renderQuoteBasket();
  updateBasketCount();

  if (basketPanel && basketOverlay) {
    basketPanel.style.display = 'block';
    basketOverlay.style.display = 'block';
    basketPanel.classList.add('open');
  }

  window.history.replaceState({}, document.title, window.location.pathname);
}
  });

  function resetPanelAndForm() {

    document.querySelectorAll('#proof-panel details').forEach(detail => {
      detail.style.display = 'block';
    });

    if (proofSuccess) proofSuccess.style.display = 'none';
    if (proofForm) proofForm.style.display = 'block';

    ['customer-name','customer-email','confirm-email','customer-mobile','logo-text']
      .forEach(id => {
        const el = document.getElementById(id);
        if (el) el.value = '';
      });

    const fileContainer = document.getElementById('file-inputs');
    if (fileContainer) {
      while (fileContainer.children.length > 1) {
        fileContainer.removeChild(fileContainer.lastChild);
      }
      const firstInput = fileContainer.querySelector('input[type="file"]');
      if (firstInput) firstInput.value = '';
    }

    document.getElementById('form-section')?.classList.remove('ready');

    proofPanel?.classList.remove('open');
    overlay?.classList.remove('open');
    document.body.classList.remove('panel-open');

    if (window.innerWidth <= 768 && proofPanel) {
      proofPanel.style.height = '0';
      proofPanel.style.maxHeight = '0';
    }

    // BOTH SIDES – reset checkboxes
    const bothCheckbox = document.getElementById('both-sides-checkbox');
    const sameCheckbox = document.getElementById('same-artwork-checkbox');
    const sameWrapper = document.getElementById('same-artwork-wrapper');
    if (bothCheckbox) bothCheckbox.checked = false;
    if (sameCheckbox) sameCheckbox.checked = false;
    if (sameWrapper) sameWrapper.style.display = 'none';
    bothSidesChecked = false;
    sameArtworkChecked = false;
  }

  async function getUKBankHolidays() {
    const cacheKey = 'ukBankHolidays';
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      return JSON.parse(cached).map(d => new Date(d));
    }

    try {
      const res = await fetch('https://www.gov.uk/bank-holidays.json');
      const data = await res.json();

      const holidays = data['england-and-wales'].events.map(e => e.date);
      sessionStorage.setItem(cacheKey, JSON.stringify(holidays));
      return holidays.map(d => new Date(d));
    } catch (e) {
      console.warn('[Delivery] Failed to load UK bank holidays', e);
      return [];
    }
  }

  async function calculateDeliveryDate(leadTimeStr) {
    const leadDaysMatch = leadTimeStr.match(/\d+/);
    const leadDays = leadDaysMatch ? parseInt(leadDaysMatch[0]) : 10;

    const now = new Date();
    let orderDate = new Date(now);
    const hour = orderDate.getHours();

    // Cut-off logic
    if (hour >= 15) {
      orderDate.setDate(orderDate.getDate() + 1);
    }

    // Skip weekends for order date
    while (orderDate.getDay() === 0 || orderDate.getDay() === 6) {
      orderDate.setDate(orderDate.getDate() + 1);
    }

    // Production starts next working day
    let currentDate = new Date(orderDate);
    currentDate.setDate(currentDate.getDate() + 1);

    // Cached UK bank holidays
    const ukHolidays = await getUKBankHolidays();
    const holidaySet = new Set(ukHolidays.map(d => d.toDateString()));

    function isWorkingDay(date) {
      const day = date.getDay();
      if (day === 0 || day === 6) return false;
      if (holidaySet.has(date.toDateString())) return false;
      return true;
    }

    // Skip non-working days at start
    while (!isWorkingDay(currentDate)) {
      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Add the required working days
    let daysAdded = 0;
    while (daysAdded < leadDays) {
      currentDate.setDate(currentDate.getDate() + 1);
      if (isWorkingDay(currentDate)) {
        daysAdded++;
      }
    }

    return currentDate.toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  }

  async function showLeadtimeAndDelivery() {
    const leadTimeRaw = window.productMetafields?.lead_time || ['Not selected'];
    const leadTime = Array.isArray(leadTimeRaw) ? leadTimeRaw[0] : leadTimeRaw;

    const deliveryDisplay = document.getElementById('estimated-delivery');
    if (deliveryDisplay) {
      deliveryDisplay.textContent = 'Calculating estimated delivery...';
      try {
        const dateStr = await calculateDeliveryDate(leadTime);
        deliveryDisplay.textContent = 'Estimated delivery date: ' + dateStr;
      } catch (err) {
        deliveryDisplay.textContent = 'Estimated delivery unavailable';
        console.error('[Delivery] Calculation error:', err);
      }
    }
  }

  // VAT toggle
  const vatToggle = document.getElementById('vat-toggle');
  let vatOn = false;

  if (vatToggle) {
    vatToggle.addEventListener('click', () => {
      vatOn = !vatOn;
      vatToggle.textContent = vatOn ? 'Click Exc. VAT' : 'Click Inc. VAT';
      updatePrice();
    });
  }

 // ====================== PACK STUDIO FLEXIBLE QUOTE AUTO-ADD ======================
async function runKaboomFlexibleQuoteAutoAdd() {
  const params = new URLSearchParams(window.location.search);

  if (params.get('kflex') !== '1') return;

  const rawQueue = localStorage.getItem('kaboomFlexibleQuoteQueue');
  if (!rawQueue) return;

  let queue;

  try {
    queue = JSON.parse(rawQueue);
  } catch (e) {
    console.error('[Flexible Pack] Invalid queue data', e);
    return;
  }

  if (!queue.items || !queue.items.length) return;

  const index = queue.currentIndex || 0;
  const item = queue.items[index];

  if (!item) {
    localStorage.removeItem('kaboomFlexibleQuoteQueue');
    showKaboomPackSuccessOverlay(0);
    openKaboomQuoteBasket();
    return;
  }

  const status = document.createElement('div');
  status.id = 'kaboom-flex-auto-overlay';

  status.innerHTML = `
    <div class="kaboom-flex-auto-box">
      <div class="kaboom-flex-auto-spinner"></div>
      <h3>Adding products to your quote basket</h3>
      <p>
        Calculating item ${index + 1} of ${queue.items.length}:<br>
        <strong>${item.title || 'Product'}</strong>
      </p>
    </div>
  `;

  document.body.appendChild(status);

  if (!document.getElementById('kaboom-flex-auto-style')) {
    const style = document.createElement('style');
    style.id = 'kaboom-flex-auto-style';

    style.textContent = `
      #kaboom-flex-auto-overlay {
        position: fixed;
        inset: 0;
        z-index: 999999;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(0,0,0,0.72);
      }

      .kaboom-flex-auto-box {
        width: min(440px, calc(100% - 40px));
        padding: 34px 28px;
        border-radius: 24px;
        background: #fff;
        text-align: center;
        box-shadow: 0 20px 60px rgba(0,0,0,0.28);
      }

      .kaboom-flex-auto-spinner {
        width: 54px;
        height: 54px;
        margin: 0 auto 20px;
        border-radius: 50%;
        border: 5px solid rgba(0,0,0,0.12);
        border-top-color: #8ab7aa;
        animation: kaboomFlexAutoSpin 800ms linear infinite;
      }

      .kaboom-flex-auto-box h3 {
        margin: 0 0 10px;
        font-size: 22px;
        letter-spacing: -0.03em;
        color: #111;
      }

      .kaboom-flex-auto-box p {
        margin: 0;
        color: rgba(0,0,0,0.65);
        line-height: 1.5;
      }

      .kaboom-flex-auto-box strong {
        color: #111;
      }

      @keyframes kaboomFlexAutoSpin {
        to {
          transform: rotate(360deg);
        }
      }

      #kaboom-pack-success-overlay {
        position: fixed;
        inset: 0;
        z-index: 999999;
        display: grid;
        place-items: center;
        padding: 20px;
        background: rgba(10, 10, 10, 0.24);
        backdrop-filter: blur(10px);
      }

      .kaboom-pack-success-card {
        width: min(460px, 100%);
        padding: 34px 30px;
        border-radius: 30px;
        background: rgba(255,255,255,0.94);
        text-align: center;
        box-shadow: 0 30px 90px rgba(0,0,0,0.24);
        animation: kaboomPackSuccessIn 420ms cubic-bezier(.2,.8,.2,1);
      }

      .kaboom-pack-success-tick {
        width: 68px;
        height: 68px;
        margin: 0 auto 18px;
        border-radius: 999px;
        display: grid;
        place-items: center;
        background: #111;
        color: #fff;
        font-size: 36px;
        font-weight: 800;
      }

      .kaboom-pack-success-eyebrow {
        margin: 0 0 8px;
        font-size: 12px;
        font-weight: 800;
        letter-spacing: .12em;
        text-transform: uppercase;
        color: rgba(0,0,0,.55);
      }

      .kaboom-pack-success-card h2 {
        margin: 0 0 20px;
        font-size: clamp(30px, 5vw, 42px);
        line-height: .95;
        letter-spacing: -.06em;
        color: #111;
      }

      .kaboom-pack-success-list {
        display: grid;
        gap: 9px;
        margin-bottom: 26px;
        color: rgba(0,0,0,.72);
        font-weight: 700;
      }

      .kaboom-pack-success-actions {
        display: grid;
        gap: 10px;
      }

      .kaboom-pack-success-actions button,
      .kaboom-pack-success-actions a {
        min-height: 50px;
        border: 0;
        border-radius: 999px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-weight: 800;
        text-decoration: none;
        cursor: pointer;
      }

      #kaboom-review-quote {
        background: #111;
        color: #fff;
      }

      #kaboom-continue-shopping {
        background: #f2f2f2;
        color: #111;
      }

      @keyframes kaboomPackSuccessIn {
        from {
          opacity: 0;
          transform: translateY(16px) scale(.96);
        }

        to {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
      }
    `;

    document.head.appendChild(style);
  }

  function wait(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  function setNativeValue(element, value) {
    if (!element) return;

    const valueSetter = Object.getOwnPropertyDescriptor(element, 'value')?.set;
    const prototype = Object.getPrototypeOf(element);
    const prototypeValueSetter = Object.getOwnPropertyDescriptor(prototype, 'value')?.set;

    if (prototypeValueSetter && valueSetter !== prototypeValueSetter) {
      prototypeValueSetter.call(element, value);
    } else if (valueSetter) {
      valueSetter.call(element, value);
    } else {
      element.value = value;
    }

    element.dispatchEvent(new Event('input', { bubbles: true }));
    element.dispatchEvent(new Event('change', { bubbles: true }));
  }

  await wait(600);

  if (numberInput && item.quantity) {
    setNativeValue(numberInput, item.quantity);
    numberInput.dispatchEvent(new Event('blur', { bubbles: true }));
  }

  await wait(300);

  const colourSelect = document.getElementById('calc-colour');

  if (colourSelect && item.colour) {
    const colourOption = Array.from(colourSelect.options).find(opt =>
      opt.value.trim().toLowerCase() === String(item.colour).trim().toLowerCase()
    );

    if (colourOption) {
      colourSelect.value = colourOption.value;
      colourSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  await wait(300);

  if (decoSelect && item.decoration) {
    updateDecorationOptions();

    const decoOption = Array.from(decoSelect.options).find(opt =>
      opt.value.trim().toLowerCase() === String(item.decoration).trim().toLowerCase()
    );

    if (decoOption && !decoOption.disabled) {
      decoSelect.value = decoOption.value;
      decoSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  await wait(600);

  updateDecorationOptions();
  updatePrice();

  await wait(700);

  const leadTimeRaw = window.productMetafields?.lead_time || ['Not selected'];
  const leadTime = Array.isArray(leadTimeRaw) ? leadTimeRaw[0] : leadTimeRaw;

  let deliveryDate = 'Not available';

  try {
    deliveryDate = await calculateDeliveryDate(leadTime);
  } catch (e) {
    console.warn('[Flexible Pack] Delivery calculation failed', e);
  }

  const unitFullText = document.querySelector('#calc-price-display .unit-price')?.textContent || 'N/A';
  const totalFullText = document.querySelector('#calc-price-display .total-price')?.textContent || 'N/A';

  const cleanTotal = window.currentRoundedUnitPrice * window.currentQty;
  const vatAmount = Math.round(cleanTotal * 0.2 * 100) / 100;
  const grandTotal = Math.round(cleanTotal * 1.2 * 100) / 100;

  const quoteMf = window.productMetafields || {};
  const quoteQtyBreaks = Array.isArray(quoteMf.quantity_breaks)
    ? quoteMf.quantity_breaks.map(q => parseInt(q, 10)).filter(Boolean)
    : [];

  const quoteMinQty = quoteQtyBreaks.length ? quoteQtyBreaks[0] : 1;
  const quoteMaxQty = quoteQtyBreaks.length ? quoteQtyBreaks[quoteQtyBreaks.length - 1] : 10000;
  const quoteIncrement = parseInt(window.productIncrement, 10) || 1;

  let decoText = decoSelect.value
    ? decoSelect.options[decoSelect.selectedIndex].text
    : formatPackFlexDecoration(item.decoration || 'Not selected');

  if (window.decorationGuidance) {
    decoText += ` ${window.decorationGuidance}`;
  }

  const quote = {
    product: window.product?.title || item.title || document.querySelector('h1')?.textContent?.trim() || 'Product',
    colour: document.getElementById('calc-colour')?.value || item.colour || 'Not selected',
    decoration: decoText,
    text: queue.customerData?.artworkNote || queue.customerData?.logoText || 'Selected in Pack Studio - flexible quantity transfer',
    leadtimeRaw: leadTimeRaw,
    quantity: numberInput?.value || item.quantity || 'Not selected',
    unitCost: unitFullText.replace(/^Unit Price:\s*/, '').trim(),
    totalExc: totalFullText.replace(/^Total:\s*/, '').trim(),
    vat: '£' + vatAmount.toFixed(2),
    totalInc: '£' + grandTotal.toFixed(2),
    customerName: queue.customerData?.name || '',
    customerEmail: queue.customerData?.email || '',
    customerMobile: queue.customerData?.mobile || '',
    imageUrl: getProductImageUrl(),
    artworkUrls: Array.isArray(queue.customerData?.artworkUrls) && queue.customerData.artworkUrls.length
      ? queue.customerData.artworkUrls
      : ['No files uploaded'],
    productHandle: item.handle,
    decorationValue: item.decoration || '',
    minQty: quoteMinQty,
    maxQty: quoteMaxQty,
    increment: quoteIncrement,
    baseCostWithFixed: window.currentBaseCostWithFixed || 0,
    individualTotalExc: window.currentIndividualTotalExc || 0,
    bothSides: window.currentBothSides || false,
    sameArtwork: window.currentSameArtwork || false,
    umbrellaPanels: window.currentUmbrellaPanels || 'none',
    fromPackStudio: true,
    estimatedDelivery: deliveryDate,
    deliveryDate: deliveryDate
  };

  addToQuoteBasket(quote);

  queue.currentIndex = index + 1;

  if (queue.currentIndex >= queue.items.length) {
    const productCount = queue.items.length;

    localStorage.removeItem('kaboomFlexibleQuoteQueue');
    localStorage.removeItem('kaboomFlexiblePackTransfer');

    if (status) status.remove();

    await wait(300);

    openKaboomQuoteBasket();
    showKaboomPackSuccessOverlay(productCount);

    return;
  }

  localStorage.setItem('kaboomFlexibleQuoteQueue', JSON.stringify(queue));

  const next = queue.items[queue.currentIndex];

  await wait(500);

  window.location.href = `/products/${next.handle}?kflex=1`;
}

function openKaboomQuoteBasket() {

  const overlay = document.getElementById('quote-basket-overlay');
  const panel = document.getElementById('quote-basket-panel');

  if (overlay) {
    overlay.style.display = 'block';
  }

  if (panel) {
    panel.style.display = 'block';
    panel.classList.add('open');
  }

}

function showKaboomPackSuccessOverlay(productCount) {
  const existing = document.getElementById('kaboom-pack-success-overlay');
  if (existing) existing.remove();

  const overlay = document.createElement('div');
  overlay.id = 'kaboom-pack-success-overlay';

  overlay.innerHTML = `
    <div class="kaboom-pack-success-card">
      <div class="kaboom-pack-success-tick">✓</div>

      <p class="kaboom-pack-success-eyebrow">
        Pack successfully transferred
      </p>

      <h2>Your Pack Is Ready</h2>

      <div class="kaboom-pack-success-list">
        <div>✓ ${productCount} products added</div>
        <div>✓ Quantities customised</div>
        <div>✓ Ready for quotation</div>
      </div>

      <div class="kaboom-pack-success-actions">
        <button type="button" id="kaboom-review-quote">
          View My Quote
        </button>

        <a href="/collections/all" id="kaboom-continue-shopping">
          Continue Shopping
        </a>
      </div>
    </div>
  `;

  document.body.appendChild(overlay);

  document.getElementById('kaboom-review-quote')?.addEventListener('click', () => {
  overlay.remove();

  openKaboomQuoteBasket();

  document.body.classList.add('kaboom-reviewing-quote');
});
}

function formatPackFlexDecoration(value) {
  if (!value) return 'Not selected';

  return String(value)
    .replace(/-/g, ' ')
    .replace(/\b\w/g, letter => letter.toUpperCase());
}

const kaboomFlexParams = new URLSearchParams(window.location.search);

if (kaboomFlexParams.get('kflex') === '1') {
  setTimeout(runKaboomFlexibleQuoteAutoAdd, 1200);
}

initBothSidesCheckboxes();
initUmbrellaPanels();
showLeadtimeAndDelivery();
updateDecorationOptions();
updatePrice();

});

// ====================== NO ARTWORK YET LOGIC ======================
function setupNoArtworkToggle() {
  const checkbox = document.getElementById('no-artwork-yet');
  const logoTextInput = document.getElementById('logo-text');
  const fileInputsContainer = document.getElementById('file-inputs');
  const uploadStatus = document.getElementById('upload-status');
  const addAnotherBtn = document.querySelector('#upload-section button[onclick="addFileInput()"]');
  const orTextSection = document.querySelector('#upload-section label[for="logo-text"]')?.parentElement;

  if (!checkbox) return;

  function toggleNoArtworkMode() {
    const isChecked = checkbox.checked;

    if (isChecked) {
      if (logoTextInput) {
        logoTextInput.style.display = 'none';
        logoTextInput.value = "No artwork provided yet - please just send me the PDF quote(s)";
      }

      if (fileInputsContainer) fileInputsContainer.style.display = 'none';
      if (uploadStatus) uploadStatus.style.display = 'none';
      if (addAnotherBtn) addAnotherBtn.style.display = 'none';
      if (orTextSection) orTextSection.style.display = 'none';

    } else {
      if (logoTextInput) {
        logoTextInput.style.display = '';
        logoTextInput.value = '';
      }

      if (fileInputsContainer) fileInputsContainer.style.display = '';
      if (addAnotherBtn) addAnotherBtn.style.display = '';
      if (orTextSection) orTextSection.style.display = '';
    }

    if (typeof window.updateReadyState === 'function') {
      window.updateReadyState();
    }
  }

  checkbox.addEventListener('change', toggleNoArtworkMode);
}

window.updateReadyState = function() {
  const noArtworkCheckbox = document.getElementById('no-artwork-yet');
  const isNoArtworkMode = noArtworkCheckbox?.checked === true;

  const fileInputs = document.querySelectorAll('input[name="attachment[]"]');

  let hasFiles = false;

  for (let input of fileInputs) {
    if (input.files && input.files.length > 0) {
      hasFiles = true;
      break;
    }
  }

  const logoTextInput = document.getElementById('logo-text');
  const hasText = logoTextInput && logoTextInput.value.trim().length > 0;

  const formSection = document.getElementById('form-section');
  const summary = formSection?.querySelector('summary');

  if (!formSection) return;

  if (isNoArtworkMode || hasFiles || hasText) {
    formSection.classList.add('ready');

    if (summary) {
      summary.style.cursor = 'pointer';
      summary.style.opacity = '1';
    }
  } else {
    formSection.classList.remove('ready');

    if (summary) {
      summary.style.cursor = 'not-allowed';
      summary.style.opacity = '0.6';
    }
  }
};

document.addEventListener('input', function(e) {
  if (e.target && e.target.id === 'logo-text') {
    window.updateReadyState?.();
  }
});

document.addEventListener('change', function(e) {
  if (
    e.target &&
    (
      e.target.id === 'logo-text' ||
      e.target.id === 'no-artwork-yet' ||
      e.target.name === 'attachment[]'
    )
  ) {
    window.updateReadyState?.();
  }
});

// Colour sync
document.addEventListener('DOMContentLoaded', function() {
  const colourRadios = document.querySelectorAll('input[type="radio"][name^="Colour"]');
  const colourInput = document.getElementById('selected-colour');

  if (colourRadios.length > 0 && colourInput) {
    colourRadios.forEach(radio => {
      radio.addEventListener('change', function() {
        if (radio.checked) {
          const colourName = radio.value || radio.ariaLabel || 'Not selected';
          colourInput.value = colourName;
        }
      });
    });

    const checkedRadio = Array.from(colourRadios).find(r => r.checked);
    if (checkedRadio) {
      const colourName = checkedRadio.value || checkedRadio.ariaLabel || 'Not selected';
      colourInput.value = colourName;
    }
  }
});

// Populate calc-colour dropdown
document.addEventListener('DOMContentLoaded', function() {
  const colourRadios = document.querySelectorAll('input[type="radio"][name^="Colour"]');
  const colourSelect = document.getElementById('calc-colour');

  if (colourSelect && colourRadios.length > 0) {
    colourSelect.innerHTML = '';

    const placeholderOption = document.createElement('option');
    placeholderOption.value = '';
    placeholderOption.textContent = 'Choose Product Colour';
    placeholderOption.disabled = true;
    placeholderOption.selected = true;
    colourSelect.appendChild(placeholderOption);

    const seen = new Set();

    colourRadios.forEach(radio => {
      const colourName = radio.value;

      if (colourName && !seen.has(colourName)) {
        seen.add(colourName);

        const opt = document.createElement('option');
        opt.value = colourName;
        opt.textContent = colourName;
        opt.dataset.variantId = radio.dataset.variantId;

        colourSelect.appendChild(opt);
      }
    });

    colourSelect.addEventListener('change', function() {
      if (colourSelect.value === '') return;

      const selected = colourSelect.value;
      const matchingRadio = Array.from(colourRadios).find(r => r.value === selected);

      if (matchingRadio) {
        matchingRadio.checked = true;
        matchingRadio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }
});

// Force main image to admin first image on load
setTimeout(() => {
  if (!window.location.pathname.includes('/products/')) return;

  const mainImg = document.querySelector('.product-media__image');

  if (mainImg && window.product && window.product.featured_image) {
    mainImg.src = window.product.featured_image.src;

    if (window.product.featured_image.srcset) {
      mainImg.srcset = window.product.featured_image.srcset;
    }

    mainImg.alt = window.product.title;
  }
}, 500);